import React, { useState, useContext, useEffect, useRef } from "react";
import { useAccountsNav } from "../../utils/moduleNav";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Search, Download, Refresh, Close } from "@mui/icons-material";
import axios from "axios";
import { format } from "date-fns";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useLedgerState } from "../../Context/LedgerStateContext";
import { useLocation } from "react-router-dom";

const PartyClosingBalances = ({ tabIndex }) => {
  const { userInfo, defaultFY, defaultFYId } =
    useContext(AutoTaskAuthContext) || {};
  const store_id = userInfo?.store_id;
  // Keeps the voucher drill-down inside the host module (pawn keeps its chrome).
  const { go: goAccounts } = useAccountsNav();
  const location = useLocation();
  const { ledgerState, updateTabState } = useLedgerState();

  const isReturningFromEntry = location.state?.fromAccountsEntry === true;
  const highlightVoucher = location.state?.highlightVoucher || null;
  // When returning, these tell us which party dialog to reopen
  const shouldReopenDialog = location.state?.reopenDialog || false;
  const dialogPartyId = location.state?.dialogPartyId || null;

  const savedState = isReturningFromEntry
    ? ledgerState.partyClosingBalances
    : null;

  // ── Main report state ──
  const [asOnDate, setAsOnDate] = useState(
    savedState?.asOnDate ?? format(new Date(), "yyyy-MM-dd"),
  );
  const [showZeroBalance, setShowZeroBalance] = useState(
    savedState?.showZeroBalance ?? false,
  );
  const [searchText, setSearchText] = useState(savedState?.searchText ?? "");
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportData, setReportData] = useState(savedState?.reportData ?? null);
  const [error, setError] = useState("");

  // ── Dialog state ──
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPartyForLedger, setSelectedPartyForLedger] = useState(
    savedState?.selectedPartyForLedger ?? null,
  );
  const [ledgerData, setLedgerData] = useState(savedState?.ledgerData ?? null);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [ledgerError, setLedgerError] = useState("");
  const [dialogMonthFilter, setDialogMonthFilter] = useState(
    savedState?.dialogMonthFilter ?? "",
  );

  // ── Ref to scroll highlighted txn row inside dialog ──
  const highlightTxnRowRef = useRef(null);
  // ── Ref to track whether we already auto-reopened (prevent double fire) ──
  const reopenHandledRef = useRef(false);

  // ── Restore dialog when returning from accounts-entry ──
  // We wait until reportData is available AND we have a dialogPartyId
  useEffect(() => {
    if (
      !shouldReopenDialog ||
      !dialogPartyId ||
      !reportData ||
      reopenHandledRef.current
    )
      return;

    const party = reportData.parties?.find((p) => p.party_id === dialogPartyId);
    if (party) {
      reopenHandledRef.current = true;
      // Restore saved ledger data immediately (no re-fetch needed) then open dialog
      if (savedState?.ledgerData) {
        setSelectedPartyForLedger(party);
        setLedgerData(savedState.ledgerData);
        setDialogMonthFilter(savedState.dialogMonthFilter ?? "");
        setDialogOpen(true);
      } else {
        // Fallback: re-fetch
        openPartyLedgerDialog(party);
      }
    }
  }, [shouldReopenDialog, dialogPartyId, reportData]);

  // ── Scroll to highlighted row inside the dialog once ledgerData+dialog are ready ──
  useEffect(() => {
    if (
      dialogOpen &&
      highlightVoucher &&
      ledgerData &&
      highlightTxnRowRef.current
    ) {
      setTimeout(() => {
        highlightTxnRowRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 200);
    }
  }, [dialogOpen, highlightVoucher, ledgerData]);

  // ── Persist state to context on every relevant change ──
  useEffect(() => {
    updateTabState(tabIndex, {
      asOnDate,
      showZeroBalance,
      searchText,
      reportData,
      selectedPartyForLedger,
      ledgerData,
      dialogMonthFilter,
    });
  }, [
    asOnDate,
    showZeroBalance,
    searchText,
    reportData,
    selectedPartyForLedger,
    ledgerData,
    dialogMonthFilter,
    tabIndex,
    updateTabState,
  ]);

  // ── Generate Report ──
  const handleGenerateReport = async () => {
    setError("");
    if (!asOnDate) {
      setError("Please select a date");
      return;
    }

    try {
      setLoadingReport(true);
      setReportData(null);

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/accounts/party-closing-balances`,
        {
          store_id: parseInt(store_id),
          as_on_date: asOnDate,
          show_zero_balance: showZeroBalance,
        },
      );

      if (response.data.success) {
        setReportData(response.data.data);
        setSearchText("");
      } else {
        setError(response.data.message || "Failed to generate report");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Error generating party closing balances",
      );
    } finally {
      setLoadingReport(false);
    }
  };

  const handleReset = () => {
    setReportData(null);
    setError("");
    setSearchText("");
    setShowZeroBalance(false);
  };

  // ── Open party ledger dialog (fetches fresh data) ──
  const openPartyLedgerDialog = async (party) => {
    setSelectedPartyForLedger(party);
    setDialogOpen(true);
    setLedgerError("");
    setLedgerData(null);
    setDialogMonthFilter("");

    try {
      setLoadingLedger(true);

      const payload = {
        store_id: parseInt(store_id),
        filter_type: "Party",
        party_account_id: party.party_id || party.customer_id || party.id,
        to_date: asOnDate,
        from_date: `${defaultFY}-04-01`,
        fy_id: defaultFYId,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/accounts/individual-ledger`,
        payload,
      );

      if (response.data.success) {
        setLedgerData(response.data.data);
      } else {
        setLedgerError(response.data.message || "Failed to fetch ledger");
      }
    } catch (err) {
      setLedgerError(
        err.response?.data?.message || "Error fetching party ledger",
      );
    } finally {
      setLoadingLedger(false);
    }
  };

  // ── Double-click row in main table → open dialog ──
  const handleRowDoubleClick = (party) => {
    reopenHandledRef.current = false; // allow future reopens
    openPartyLedgerDialog(party);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    // Keep selectedPartyForLedger & ledgerData in state so they persist
    // (they are already in context via the useEffect above)
  };

  // ── Double-click transaction row inside dialog → navigate to accounts-entry ──
  const handleTxnRowDoubleClick = (txn) => {
    // Pass back everything needed to restore this exact state
    goAccounts("/accounts-entry", {
      state: {
        voucher_no: txn.voucher_number,
        fromAccountsEntry: false, // this flag is checked on the accounts-entry side
        fromLedger: true,
        // These come back via location.state when user navigates back to /ledger
        highlightVoucher: txn.voucher_number,
        reopenDialog: true,
        dialogPartyId: selectedPartyForLedger?.party_id,
      },
    });
  };

  // ── Month filter ──
  const filteredTransactions = React.useMemo(() => {
    if (!ledgerData?.transactions) return [];

    const sorted = [...ledgerData.transactions].sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );

    if (!dialogMonthFilter) return sorted;

    return sorted.filter((txn) => {
      const txnMonth = format(new Date(txn.date), "yyyy-MM");
      return txnMonth === dialogMonthFilter;
    });
  }, [ledgerData, dialogMonthFilter]);

  // ── Format helpers ──
  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);

  const formatAmount = (amount) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  // ── Client-side search filter ──
  const filteredParties =
    reportData?.parties?.filter((p) => {
      if (!searchText.trim()) return true;
      const q = searchText.toLowerCase();
      return (
        p.party_name?.toLowerCase().includes(q) ||
        p.party_code?.toLowerCase().includes(q) ||
        p.party_mobile?.includes(q)
      );
    }) || [];

  const filteredDebitTotal = filteredParties.reduce(
    (sum, p) => sum + p.net_debit,
    0,
  );
  const filteredCreditTotal = filteredParties.reduce(
    (sum, p) => sum + p.net_credit,
    0,
  );

  // ── PDF Generation ──
  const generatePDF = () => {
    if (!reportData) return;
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const storeName = userInfo?.store_name || "";

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(storeName, pageWidth / 2, 12, { align: "center" });
    doc.setFontSize(9);
    doc.text("Party Closing Balances", pageWidth / 2, 18, { align: "center" });
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(`As On: ${format(new Date(asOnDate), "dd-MM-yyyy")}`, margin, 24);
    doc.text(
      `Total Parties: ${filteredParties.length}`,
      pageWidth - margin - 30,
      24,
    );
    doc.setFont("helvetica", "bold");
    doc.text(`Debit Total: ${formatAmount(filteredDebitTotal)}`, margin, 29);
    doc.text(
      `Credit Total: ${formatAmount(filteredCreditTotal)}`,
      margin + 60,
      29,
    );
    doc.setFont("helvetica", "normal");

    const tableHead = [
      ["#", "Party Code", "Last Txn Date", "Name", "Mobile", "Debit", "Credit"],
    ];
    const tableData = filteredParties.map((party, idx) => [
      String(idx + 1),
      party.party_code || "-",
      party.last_txn_date
        ? format(new Date(party.last_txn_date), "dd-MM-yyyy")
        : "-",
      party.party_name || "",
      party.party_mobile || "",
      party.net_debit > 0 ? formatAmount(party.net_debit) : "",
      party.net_credit > 0 ? formatAmount(party.net_credit) : "",
    ]);
    tableData.push([
      "",
      "",
      "",
      "Grand Total",
      "",
      filteredDebitTotal > 0 ? formatAmount(filteredDebitTotal) : "",
      filteredCreditTotal > 0 ? formatAmount(filteredCreditTotal) : "",
    ]);

    doc.autoTable({
      startY: 33,
      head: tableHead,
      body: tableData,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.5,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
        lineWidth: 0.3,
        fontSize: 9,
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 8 },
        1: { halign: "left", cellWidth: 20 },
        2: { halign: "left", cellWidth: 24 },
        3: { halign: "left", cellWidth: 62 },
        4: { halign: "left", cellWidth: 28 },
        5: { halign: "right", cellWidth: 22 },
        6: { halign: "right", cellWidth: 22 },
      },
      bodyStyles: { textColor: [0, 0, 0] },
      didParseCell: (data) => {
        if (
          data.row.index === tableData.length - 1 &&
          data.section === "body"
        ) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [245, 245, 245];
        }
        if (
          data.column.index === 5 &&
          data.cell.text[0] &&
          data.section === "body" &&
          data.row.index !== tableData.length - 1
        ) {
          data.cell.styles.textColor = [200, 0, 0];
        }
      },
      margin: { left: margin, right: margin },
    });

    doc.save(
      `PartyClosingBalances_AsOn_${format(
        new Date(asOnDate),
        "dd-MM-yyyy",
      )}.pdf`,
    );
  };

  // ── Render ──
  return (
    <Box>
      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Party Closing Balances
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2.5}>
            <TextField
              fullWidth
              type="date"
              label="As On Date"
              value={asOnDate}
              onChange={(e) => setAsOnDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <Button
              variant="contained"
              size="large"
              startIcon={
                loadingReport ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Search />
                )
              }
              onClick={handleGenerateReport}
              disabled={loadingReport}
              sx={{ mr: 2 }}
            >
              {loadingReport ? "Generating..." : "Generate Report"}
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Refresh />}
              onClick={handleReset}
              disabled={loadingReport}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Report Table */}
      {reportData && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Party Closing Balances
            </Typography>
            <Typography variant="body2" color="text.secondary">
              As On: <strong>{format(new Date(asOnDate), "dd/MM/yyyy")}</strong>
            </Typography>
            <Typography
              variant="caption"
              color="primary"
              sx={{ mt: 1, display: "block" }}
            >
              💡 Double-click on any row to view detailed ledger
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total Parties
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {reportData.total_parties}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Grand Debit Total
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: "error.main" }}
                  >
                    {formatCurrency(reportData.grand_total_debit)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Grand Credit Total
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    {formatCurrency(reportData.grand_total_credit)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <TextField
              size="small"
              placeholder="Search by name, party code or mobile..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ minWidth: 320 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={generatePDF}
            >
              Download PDF
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Party Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last Txn Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Party Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Mobile</TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, color: "error.main" }}
                  >
                    Debit
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Credit
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredParties.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No parties found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {filteredParties.map((party, index) => (
                      <TableRow
                        key={party.party_id}
                        hover
                        onDoubleClick={() => handleRowDoubleClick(party)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "action.hover" },
                        }}
                      >
                        <TableCell
                          sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                        >
                          {index + 1}
                        </TableCell>
                        <TableCell>{party.party_code || "-"}</TableCell>
                        <TableCell>
                          {party.last_txn_date
                            ? format(
                                new Date(party.last_txn_date),
                                "dd/MM/yyyy",
                              )
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {party.party_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {party.party_mobile}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {party.net_debit > 0 ? (
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "error.main" }}
                            >
                              {formatCurrency(party.net_debit)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {party.net_credit > 0 ? (
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "text.primary" }}
                            >
                              {formatCurrency(party.net_credit)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ backgroundColor: "grey.100" }}>
                      <TableCell colSpan={5}>
                        <strong>
                          Grand Total ({filteredParties.length} parties
                          {searchText ? " — filtered" : ""})
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{ fontWeight: 700, color: "error.main" }}
                        >
                          {formatCurrency(filteredDebitTotal)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{ fontWeight: 700, color: "text.primary" }}
                        >
                          {formatCurrency(filteredCreditTotal)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {!reportData && !error && !loadingReport && (
        <Paper elevation={1} sx={{ p: 6, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            Select a date and click "Generate Report" to view party closing
            balances
          </Typography>
        </Paper>
      )}

      {/* ── DIALOG: INDIVIDUAL PARTY LEDGER ── */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: "80vh" } }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Party Ledger — {selectedPartyForLedger?.party_name}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <Close />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Mobile: {selectedPartyForLedger?.party_mobile} | Party Code:{" "}
            {selectedPartyForLedger?.party_code || "N/A"}
          </Typography>
          <Typography variant="caption" color="primary">
            All transactions — use month filter to narrow down
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {loadingLedger && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {ledgerError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {ledgerError}
            </Alert>
          )}

          {ledgerData && !loadingLedger && (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Opening Balance
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {formatCurrency(ledgerData.opening_balance)}
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          display: "inline-block",
                          px: 1,
                          py: 0.2,
                          borderRadius: 1,
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: "white",
                          backgroundColor: "primary.main",
                        }}
                      >
                        {ledgerData.opening_balance_type}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Total Debit
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "error.main" }}
                      >
                        {formatCurrency(ledgerData.total_debit)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        {ledgerData.transaction_count} transactions
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Total Credit
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        {formatCurrency(ledgerData.total_credit)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        {ledgerData.transaction_count} transactions
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Month Filter */}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <TextField
                  type="month"
                  size="small"
                  label="Filter by Month"
                  value={dialogMonthFilter}
                  onChange={(e) => setDialogMonthFilter(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    width: 200,
                    "& input::-webkit-datetime-edit": {
                      color: dialogMonthFilter ? "inherit" : "transparent",
                    },
                    "& input:focus::-webkit-datetime-edit": {
                      color: "inherit",
                    },
                  }}
                />
                {dialogMonthFilter && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setDialogMonthFilter("")}
                  >
                    Clear Filter
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary">
                  Showing {filteredTransactions.length} of{" "}
                  {ledgerData.transactions.length} transactions
                </Typography>
              </Box>

              {/* Transactions Table */}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Voucher No.
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Narration</TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, color: "error.main" }}
                      >
                        Debit
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        Credit
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Balance
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Opening Balance Row */}
                    <TableRow sx={{ backgroundColor: "grey.50" }}>
                      <TableCell colSpan={3}>
                        <strong>Opening Balance</strong>
                      </TableCell>
                      <TableCell align="right">-</TableCell>
                      <TableCell align="right">-</TableCell>
                      <TableCell align="right">
                        <Typography>
                          {formatCurrency(ledgerData.opening_balance)}
                        </Typography>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            ml: 0.5,
                            px: 0.5,
                            py: 0.5,
                            borderRadius: 20,
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            lineHeight: 1,
                            color: "white",
                            backgroundColor: "primary.main",
                          }}
                        >
                          {ledgerData.opening_balance_type}
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Transaction Rows */}
                    {filteredTransactions.length > 0 ? (
                      filteredTransactions.map((txn, index) => {
                        const isHighlighted =
                          highlightVoucher === txn.voucher_number;
                        return (
                          <Tooltip
                            key={index}
                            title="Double-click to open voucher"
                            placement="left"
                          >
                            <TableRow
                              ref={isHighlighted ? highlightTxnRowRef : null}
                              hover
                              onDoubleClick={() => handleTxnRowDoubleClick(txn)}
                              sx={{
                                cursor: "pointer",
                                backgroundColor: isHighlighted
                                  ? "rgba(4, 126, 207, 0.15)"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor: isHighlighted
                                    ? "rgba(4, 126, 207, 0.25)"
                                    : "action.hover",
                                },
                              }}
                            >
                              <TableCell>
                                {format(new Date(txn.date), "dd/MM/yyyy")}
                              </TableCell>
                              <TableCell>{txn.voucher_number}</TableCell>
                              <TableCell>{txn.narration}</TableCell>
                              <TableCell
                                align="right"
                                sx={{ color: "error.main" }}
                              >
                                {txn.debit_amount > 0
                                  ? formatCurrency(txn.debit_amount)
                                  : "-"}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ color: "text.primary" }}
                              >
                                {txn.credit_amount > 0
                                  ? formatCurrency(txn.credit_amount)
                                  : "-"}
                              </TableCell>
                              <TableCell align="right">
                                <strong>
                                  {formatCurrency(txn.running_balance)}
                                </strong>{" "}
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-block",
                                    ml: 0.5,
                                    px: 0.5,
                                    py: 0.5,
                                    borderRadius: 20,
                                    fontSize: "0.6rem",
                                    fontWeight: 600,
                                    lineHeight: 1,
                                    color: "white",
                                    backgroundColor: "primary.main",
                                  }}
                                >
                                  {txn.balance_type}
                                </Box>
                              </TableCell>
                            </TableRow>
                          </Tooltip>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">
                            {dialogMonthFilter
                              ? "No transactions for selected month"
                              : "No transactions found"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Closing Balance Row */}
                    <TableRow sx={{ backgroundColor: "grey.50" }}>
                      <TableCell colSpan={3}>
                        <strong>Closing Balance</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {formatCurrency(ledgerData.total_debit)}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {formatCurrency(ledgerData.total_credit)}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {formatCurrency(ledgerData.closing_balance)}
                        </strong>{" "}
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            ml: 0.5,
                            px: 0.5,
                            py: 0.5,
                            borderRadius: 20,
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            lineHeight: 1,
                            color: "white",
                            backgroundColor: "primary.main",
                          }}
                        >
                          {ledgerData.closing_balance_type}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartyClosingBalances;
